import { Card } from '../../../components/ui/Card';
import { formatCurrency } from '../../../lib/utils';

interface TopProduct {
  id: number;
  nombre: string;
  cantidadVendida: number;
  totalVendido: number;
  precio: number;
  categoria: string;
  imagenUrl?: string | null;
}

interface TopProductsProps {
  products: TopProduct[];
  isLoading?: boolean;
}

export function TopProducts({ products, isLoading = false }: TopProductsProps) {
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-40">
          <div className="text-gray-500">Cargando productos más vendidos...</div>
        </div>
      </Card>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          Productos Más Vendidos
        </h3>
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No hay datos disponibles</p>
          <p className="text-xs text-gray-400 mt-1">Aún no se han registrado ventas completadas</p>
        </div>
      </Card>
    );
  }

  const maxTotal = Math.max(...products.map(p => p.totalVendido));

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          Productos Más Vendidos
        </h3>
        <span className="text-xs text-gray-400">
          Top {products.length} productos
        </span>
      </div>
      <div className="space-y-4">
        {products.map((product, index) => {
          const porcentaje = maxTotal > 0 ? (product.totalVendido / maxTotal) * 100 : 0;
          
          const colors = [
            'bg-yellow-500',
            'bg-gray-400',
            'bg-amber-600',
            'bg-blue-500',
            'bg-green-500',
            'bg-purple-500',
            'bg-pink-500',
            'bg-indigo-500',
            'bg-red-500',
            'bg-teal-500',
          ];

          return (
            <div key={product.id} className="group">
              <div className="flex items-center gap-3">
                <div className="w-7 text-center">
                  <span className={`text-sm font-bold ${
                    index === 0 ? 'text-yellow-500' :
                    index === 1 ? 'text-gray-400' :
                    index === 2 ? 'text-amber-600' :
                    'text-gray-400'
                  }`}>
                    #{index + 1}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <span className="font-medium text-gray-800 text-sm">
                        {product.nombre}
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        {product.categoria}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-gray-800">
                        {formatCurrency(product.totalVendido)}
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        ({product.cantidadVendida} uds)
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full mt-1 overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all duration-700 ease-out ${
                        colors[index % colors.length]
                      }`}
                      style={{ 
                        width: `${Math.max(porcentaje, 2)}%`,
                        transition: 'width 0.8s ease-in-out'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          - Ventas completadas
        </p>
      </div>
    </Card>
  );
}